import { Badge, Button, Stack, Typography } from "@mui/material";
import { ImagePlus } from "lucide-react";
import React, { forwardRef, useEffect, useState } from "react";
import RoundContainer from "../RoundContainer";
import styled, { useTheme } from "styled-components";
import { colors, fontSizes } from "../../theme";
import { useApi } from "../../contexts/ApiContext";

interface FileUploadButtonProps {
  onChange(file: File[] | null): void;
  keepShowingUploadIcon?: boolean;
  onError?: (file: File, error: string) => void;
  accept: string;
  title?: string;
  caption?: string | string[];
  minWidth?: number;
  minHeight?: number;
}

const StyledButton = styled(Button)(({ theme }) => ({
  "& ul": {
    listStyleType: "disc",
    paddingLeft: "1rem",
    whiteSpace: "break-spaces",
  },
}));

const FileUploadButton = forwardRef<HTMLButtonElement, FileUploadButtonProps>(
  (
    {
      title = "Upload or drag and drop here",
      onChange,
      onError,
      accept,
      keepShowingUploadIcon = false,
      caption = "Image files only, 20 MB max",
      minHeight = 300,
      minWidth = 300,
    },
    ref
  ) => {
    console.info("ref", ref);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const { isSubmitting, isLoading } = useApi();
    const isDisabled = isLoading || isSubmitting;

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (event.target.files && event.target.files.length > 0) {
        console.log("event.target.files", event.target.files);
        const filesArray = Array.from(event.target.files);
        const validationPromises = filesArray.map((file) => {
          if (file.type.startsWith("image/")) {
            return checkImageDimensions(file).catch((error) => {
              if (onError) {
                onError(file, error);
              }
              return null;
            });
          } else if (file.type.startsWith("video/")) {
            return checkVideoDimensions(file).catch((error) => {
              if (onError) {
                onError(file, error);
              }
              return null; // Exclude invalid file
            });
          }
          return Promise.resolve(file);
        });

        const validatedFiles = await Promise.all(validationPromises);
        const validFiles = validatedFiles.filter(
          (file): file is File => file !== null
        );
        setSelectedFiles(validFiles.length > 0 ? validFiles : null);
      }
    };

    const checkImageDimensions = (file: File): Promise<File> => {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          if (img.width < minWidth || img.height < minHeight) {
            reject("errors.uploads.images.invalid_dimensions");
          } else {
            resolve(file);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject("Failed to load image");
        };
        img.src = url;
      });
    };

    const checkVideoDimensions = (file: File): Promise<File> => {
      console.log("checkVideoDimensions");
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          if (video.videoWidth < minWidth || video.videoHeight < minHeight) {
            reject("errors.uploads.videos.invalid_dimensions");
          } else {
            resolve(file);
          }
        };
        video.onerror = () => {
          URL.revokeObjectURL(url);
          reject("Failed to load video");
        };
        video.src = url;
      });
    };

    useEffect(() => {
      console.log("selectedFiles", selectedFiles);
      if (selectedFiles === null) {
        onChange([]);
      } else {
        onChange(selectedFiles);
      }
    }, [selectedFiles]);

    const openFileDialog = () => {
      console.log("fileInputRef.current", fileInputRef.current);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const renderCaption = () => {
      if (Array.isArray(caption)) {
        return (
          <ul>
            {caption.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        );
      }
      return caption;
    };

    return (
      <StyledButton
        size="small"
        color={"uploadButton"}
        ref={ref}
        onClick={openFileDialog}
        disabled={isDisabled}
      >
        <input
          multiple
          accept={accept}
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />
        <Stack width={"100%"} direction={"row"} gap={2} alignItems={"center"}>
          {selectedFiles && !keepShowingUploadIcon ? (
            selectedFiles.length > 1 ? (
              <Badge badgeContent={4} color="primary">
                <img
                  width={"60px"}
                  src={URL.createObjectURL(selectedFiles[0])}
                  alt="Selected file"
                  onLoad={(e) =>
                    URL.revokeObjectURL((e.target as HTMLImageElement).src)
                  }
                />
              </Badge>
            ) : (
              <img
                width={"60px"}
                src={URL.createObjectURL(selectedFiles[0])}
                alt="Selected file"
                onLoad={(e) =>
                  URL.revokeObjectURL((e.target as HTMLImageElement).src)
                }
              />
            )
          ) : (
            <RoundContainer backgroundColor="#ffffff">
              <ImagePlus color={colors.violet[900]} />
            </RoundContainer>
          )}
          <Stack spacing={0} textAlign={"left"} width={"100%"}>
            <Typography
              color={colors.stone[900]}
              fontSize={fontSizes.sm}
              variant="medium"
            >
              {title}
            </Typography>
            <Typography
              color={"#1C1917"}
              fontSize={fontSizes.sm}
              variant="normal"
            >
              {renderCaption()}
            </Typography>
          </Stack>
        </Stack>
      </StyledButton>
    );
  }
);

export default FileUploadButton;
